import { useEffect, useState } from 'react';
import type { IsoDate, Rating } from '@contracts';
import { useDomainApi } from '@ui/shared/domain/DomainApiContext';
import { BatteryLow, CloudDrizzle, Cloud, Sun, Sparkles } from '@ui/shared/icons';
import { computeMoodTrend7d, type MoodTrendPoint } from '@domain/today/analytics';
import { todayInputStyle, MicroLabel, TodayCard } from './primitives';

const RATINGS: readonly Rating[] = [1, 2, 3, 4, 5];

/** Mood-tile icon table (design/screens/today-desktop-v2.md SS7.1) — five
 *  distinct Lucide-class icons, never emoji faces (standing hard rule). */
const MOOD_TILES: ReadonlyArray<{ readonly rating: Rating; readonly label: string; readonly icon: typeof BatteryLow }> = [
  { rating: 1, label: 'Low', icon: BatteryLow },
  { rating: 2, label: 'Off', icon: CloudDrizzle },
  { rating: 3, label: 'Okay', icon: Cloud },
  { rating: 4, label: 'Good', icon: Sun },
  { rating: 5, label: 'Peak', icon: Sparkles },
];

/**
 * Row B (main) — Morning check-in (R8.5, design/screens/today-desktop-v2.md
 * SS7.1). The richest card on the screen: mood/energy (presentation change
 * only over the existing 1-5 `Rating` scale), day-summary note (reuses
 * `checkin.note_set` verbatim, D6), sleep hours (new), and a 7-day mood
 * trend mini chart (new query, no new event).
 */
export function MorningCheckInCard({
  date,
  mood,
  energy,
  note,
  sleepHours,
  onSetMood,
  onSetEnergy,
  onSetNote,
  onSetSleepHours,
}: {
  readonly date: IsoDate;
  readonly mood: Rating | null;
  readonly energy: Rating | null;
  readonly note: string;
  readonly sleepHours: number | null;
  readonly onSetMood: (next: Rating) => void;
  readonly onSetEnergy: (next: Rating) => void;
  readonly onSetNote: (next: string) => void;
  readonly onSetSleepHours: (next: number) => void;
}) {
  const api = useDomainApi();
  const [noteDraft, setNoteDraft] = useState(note);
  const [sleepDraft, setSleepDraft] = useState(sleepHours === null ? '' : String(sleepHours));
  const [trend, setTrend] = useState<readonly MoodTrendPoint[] | null>(null);

  useEffect(() => setNoteDraft(note), [note]);
  useEffect(() => setSleepDraft(sleepHours === null ? '' : String(sleepHours)), [sleepHours]);

  useEffect(() => {
    let cancelled = false;
    void api.getAllEvents().then((events) => {
      if (cancelled) return;
      setTrend(computeMoodTrend7d(events, date));
    });
    return () => {
      cancelled = true;
    };
  }, [api, date, mood]);

  function commitSleep() {
    const parsed = Number(sleepDraft);
    if (sleepDraft.trim() === '' || Number.isNaN(parsed)) return;
    const clamped = Math.min(24, Math.max(0, parsed));
    onSetSleepHours(clamped);
  }

  return (
    <TodayCard label="Morning check-in">
      <CardHeading>Morning check-in</CardHeading>

      <div style={{ marginTop: 'var(--space-4)', display: 'flex', gap: 'var(--space-6)', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 220px', minWidth: 0 }}>
          <MicroLabel>Mood</MicroLabel>
          <div role="radiogroup" aria-label="Mood" style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
            {MOOD_TILES.map((tile) => {
              const selected = mood === tile.rating;
              const Icon = tile.icon;
              return (
                <button
                  key={tile.rating}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  aria-label={`Mood: ${tile.label}`}
                  // Bug fix (owner-reported, live screenshot): re-tapping the
                  // already-selected tile used to be swallowed here entirely
                  // ("re-tap handled by caller via toggle logic upstream" --
                  // a comment describing behavior this guard itself
                  // prevented from ever running). `onSetMood` always fires
                  // now; `setMoodValue` (TodayView.desktop.tsx) is what
                  // branches into the revert-vs-set logic, same pattern the
                  // energy control below already relies on correctly.
                  onClick={() => onSetMood(tile.rating)}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '2px',
                    width: '52px',
                    padding: 'var(--space-1) 0',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid transparent',
                    background: selected
                      ? 'color-mix(in srgb, var(--hue-primary-vivid) 14%, var(--surface-raised))'
                      : 'var(--surface-raised)',
                    flex: 'none',
                  }}
                >
                  <Icon size={18} color={selected ? 'var(--hue-primary)' : 'var(--text-muted)'} aria-hidden="true" />
                  {/* Bug fix (owner-reported, live screenshot): the tile used
                      to carry its name ONLY in aria-label -- invisible to a
                      sighted user, so the selected tile read as an unlabeled
                      green icon with no way to confirm which of the 5 moods
                      it was. Same defect/fix already applied once to the
                      phone Today mood/energy row (today-view.md §1.25's
                      "icon-only identification fails in practice" finding)
                      -- this is that same lesson recurring on desktop. */}
                  <span
                    aria-hidden="true"
                    style={{
                      fontSize: '10px',
                      lineHeight: 1,
                      fontWeight: selected ? 600 : 500,
                      color: selected ? 'var(--hue-primary)' : 'var(--text-muted)',
                    }}
                  >
                    {tile.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ flex: '1 1 220px', minWidth: 0 }}>
          <MicroLabel>Energy</MicroLabel>
          <div
            role="radiogroup"
            aria-label="Energy"
            style={{ display: 'flex', gap: '2px', marginTop: 'var(--space-2)', height: 'var(--space-4)' }}
          >
            {RATINGS.map((n) => {
              const filled = energy !== null && n <= energy;
              return (
                <button
                  key={n}
                  type="button"
                  role="radio"
                  aria-checked={energy === n}
                  aria-label={`Energy: ${n}`}
                  onClick={() => onSetEnergy(n)}
                  style={{
                    flex: 1,
                    borderRadius: n === 1 ? 'var(--radius-pill) 0 0 var(--radius-pill)' : n === 5 ? '0 var(--radius-pill) var(--radius-pill) 0' : 0,
                    background: filled ? 'var(--hue-primary-vivid)' : 'var(--border-subtle)',
                  }}
                />
              );
            })}
          </div>
          <p
            aria-hidden="true"
            style={{
              margin: 'var(--space-1) 0 0',
              fontSize: 'var(--type-caption-size)',
              color: 'var(--text-muted)',
            }}
          >
            {energy === null ? 'Not set' : `${energy} of 5`}
          </p>
        </div>
      </div>

      <div style={{ marginTop: 'var(--space-4)', display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 320px', minWidth: 0 }}>
          <MicroLabel>Note</MicroLabel>
          <input
            aria-label="Day-summary note"
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value)}
            onBlur={() => onSetNote(noteDraft)}
            placeholder="Steady — coffee, a short walk, nothing dramatic."
            style={{ ...todayInputStyle, marginTop: 'var(--space-2)' }}
          />
        </div>
        <div style={{ flex: 'none', width: '96px' }}>
          <MicroLabel>Sleep</MicroLabel>
          <div style={{ marginTop: 'var(--space-2)', display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
            <input
              aria-label="Sleep hours"
              type="number"
              min={0}
              max={24}
              step={0.5}
              value={sleepDraft}
              onChange={(e) => setSleepDraft(e.target.value)}
              onBlur={commitSleep}
              style={{ ...todayInputStyle, width: '64px', padding: '0 var(--space-2)' }}
            />
            <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)' }}>hrs</span>
          </div>
        </div>
      </div>

      <div style={{ marginTop: 'var(--space-4)' }}>
        <MicroLabel>Last 7 days · mood</MicroLabel>
        {trend && <MoodTrendMiniChart points={trend} />}
      </div>
    </TodayCard>
  );
}

function CardHeading({ children }: { readonly children: string }) {
  return (
    <h2 style={{ margin: 0, fontSize: 'var(--type-heading-size)', fontWeight: 'var(--type-heading-weight)', color: 'var(--text-primary)' }}>
      {children}
    </h2>
  );
}

/** 7-bar mini chart — a gap for a day with no mood set renders as an empty
 *  outline, never a fabricated value (AC-T16). */
function MoodTrendMiniChart({ points }: { readonly points: readonly MoodTrendPoint[] }) {
  const trendLabel = trendCaption(points);
  return (
    <div style={{ marginTop: 'var(--space-2)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 'var(--space-1)', height: '36px' }}>
        {points.map((point) => (
          <div
            key={point.date}
            role="img"
            aria-label={point.mood === null ? `${point.date}: no mood set` : `${point.date}: mood ${point.mood}`}
            style={{
              flex: 1,
              height: point.mood === null ? '8px' : `${(point.mood / 5) * 36}px`,
              borderRadius: '2px',
              background: point.mood === null ? 'transparent' : 'color-mix(in srgb, var(--hue-primary) 70%, transparent)',
              border: point.mood === null ? '1px dashed var(--border-subtle)' : 'none',
              alignSelf: 'flex-end',
            }}
          />
        ))}
      </div>
      {trendLabel && (
        <p style={{ margin: 'var(--space-2) 0 0', fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)' }}>
          {trendLabel}
        </p>
      )}
    </div>
  );
}

/** Plain computed phrase, never AI-generated — the "down" case renders in the
 *  exact same tone as "up" (SS2's central ruling: no red for a declining trend). */
function trendCaption(points: readonly MoodTrendPoint[]): string | null {
  const withData = points.filter((p): p is { date: IsoDate; mood: number } => p.mood !== null);
  if (withData.length < 2) return null;
  const midpoint = Math.floor(withData.length / 2);
  const earlier = withData.slice(0, midpoint);
  const later = withData.slice(midpoint);
  if (earlier.length === 0 || later.length === 0) return null;
  const earlierAvg = earlier.reduce((sum, p) => sum + p.mood, 0) / earlier.length;
  const laterAvg = later.reduce((sum, p) => sum + p.mood, 0) / later.length;
  const delta = laterAvg - earlierAvg;
  if (Math.abs(delta) < 0.25) return 'Similar to last week';
  return delta > 0 ? 'Trending up from last week' : 'Trending down from last week';
}
